/**
 * Account API Repository
 *
 * home_karte_api `/api/v1/accounts/:id` との通信を担当
 *
 * 用途:
 *   - GET  /accounts/:id → アカウント情報取得（後払い決済画面で email 初期値に使用）
 *   - PUT  /accounts/:id → アカウント情報更新（後払い時に email を保存）
 *
 * 注: API の `updateAccount` スキーマは `accountBase` 全フィールド必須のため、
 *     email だけ部分更新は不可。GET で取得したものに email を差し替えて PUT する。
 */

import { ENV } from '../../../shared/config/env';
import { createApiClient } from '../../../shared/services/api';
import type { ApiResult } from '../../../shared/types/api';

const API_BASE_URL = ENV.API_BASE_URL;

/**
 * アカウント情報 (API レスポンス)
 * API: GET /api/v1/accounts/:id
 */
export interface Account {
  accountId: string;
  accountTypeId: string;
  name: string;
  nameKana: string;
  genderId: string;
  mobilePhoneNumber: string;
  landlinePhoneNumber?: string;
  email?: string;
  birthday?: string;
  receiveNotification?: boolean;
}

/**
 * 更新リクエスト Body (API: PUT /api/v1/accounts/:id)
 *
 * `accountBase` スキーマと一致させる必要がある。
 * receiveNotification と accountTypeId は更新対象外（別エンドポイント or 更新不可）。
 */
export interface UpdateAccountRequest {
  name: string;
  nameKana: string;
  genderId: string;
  mobilePhoneNumber: string;
  landlinePhoneNumber?: string | null;
  email?: string | null;
  birthday?: string | null;
}

export const accountRepository = {
  /**
   * アカウント情報取得
   */
  getById: async (
    accountId: string,
    token?: string,
  ): Promise<ApiResult<Account>> => {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    return client.get<Account>(`/api/v1/accounts/${accountId}`);
  },

  /**
   * アカウント情報更新
   *
   * API は全フィールド必須なので、呼び出し側で GET 結果に差分を当ててから渡す。
   */
  update: async (
    accountId: string,
    body: UpdateAccountRequest,
    token?: string,
  ): Promise<ApiResult<Account>> => {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    return client.put<Account>(`/api/v1/accounts/${accountId}`, body);
  },
};
